// urlConfig.js - Utility for parsing and handling URL-based product configuration

/**
 * Parse URL to extract product code and material configuration
 * Supports formats:
 * - /PRODUCTCODE?material1=value1&material2=value2
 * - /?code=PRODUCTCODE&material1=value1&material2=value2
 * - /index.html?code=PRODUCTCODE&material1=value1
 *
 * @returns {Object} { productCode: string, materialConfig: Object }
 */
export function parseUrlConfig() {
  const url = new URL(window.location.href)
  const pathname = url.pathname
  const searchParams = url.searchParams

  let productCode = null
  const materialConfig = {}

  // Method 1: Extract product code from path (e.g., /C5HU4KD9WK)
  // Look for alphanumeric code in path
  const pathParts = pathname.split('/').filter(p => p.length > 0)
  const lastPart = pathParts[pathParts.length - 1]

  // Check if last part looks like a product code (alphanumeric, typically 10 chars)
  if (lastPart && /^[A-Z0-9]{10}$/i.test(lastPart)) {
    productCode = lastPart.toUpperCase()
  }

  // Method 2: Extract from query parameter 'code'
  if (!productCode && searchParams.has('code')) {
    productCode = searchParams.get('code').toUpperCase()
  }

  // Extract all material parameters (any param that's not 'code', 'admin', 'language')
  const reservedParams = ['code', 'admin', 'language']

  for (const [key, value] of searchParams.entries()) {
    if (!reservedParams.includes(key.toLowerCase())) {
      // Key is the material type (technical name)
      // Value is the material name or slug
      materialConfig[key] = value
    }
  }

  return {
    productCode,
    materialConfig
  }
}

/**
 * Generate a URL for a configured product
 * @param {string} productCode - Product code
 * @param {Object} materialSelections - Object with material type as key and material name as value
 * @param {boolean} includeBaseUrl - Whether to include full base URL
 * @returns {string} Generated URL
 */
export function generateConfigUrl(productCode, materialSelections = {}, includeBaseUrl = false) {
  const baseUrl = includeBaseUrl ? window.location.origin : ''
  const params = new URLSearchParams()

  // Add material selections as query parameters
  Object.entries(materialSelections).forEach(([type, material]) => {
    if (material && (material.name || material.slug)) {
      // Use slug if available, otherwise use name
      const value = material.slug || material.name
      params.append(type, value)
    }
  })

  const queryString = params.toString()
  const url = `${baseUrl}/${productCode}${queryString ? '?' + queryString : ''}`

  return url
}

/**
 * Update browser URL without reload
 * @param {string} productCode - Product code
 * @param {Object} materialSelections - Current material selections
 */
export function updateUrlWithConfig(productCode, materialSelections = {}) {
  const url = generateConfigUrl(productCode, materialSelections, false)

  // Use history.replaceState to update URL without reload
  window.history.replaceState(
    { productCode, materialSelections },
    '',
    url
  )
}

/**
 * Check if URL has configuration parameters
 * @returns {boolean}
 */
export function hasUrlConfig() {
  const { productCode, materialConfig } = parseUrlConfig()
  return productCode !== null || Object.keys(materialConfig).length > 0
}
